/**
 * CL-409D — Admin-controlled project journey (5 stages, manual status).
 */

import {
  JOURNEY_MILESTONE_STATE,
  computeJourneyProgressPercent,
  resolveProjectJourneyThumbnailUrl,
} from "./projectJourney";

export const PROJECT_JOURNEY_STAGE_STATUS = Object.freeze({
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
});

export const PROJECT_JOURNEY_STAGE_STATUS_LABELS = Object.freeze({
  not_started: "Not started",
  in_progress: "In progress",
  complete: "Complete",
});

export const PROJECT_JOURNEY_STAGE_DEFS = [
  {
    key: "concept_design",
    label: "Concept Design",
    stageTitle: "Concept Design",
    description: "Exploring design direction and early concepts for your project.",
    tooltipExplanation: "Concept sketches and options for your feedback.",
  },
  {
    key: "permit_submission",
    label: "Permit Submission",
    stageTitle: "Permit Submission",
    description: "Application packages are prepared and submitted to the city.",
    tooltipExplanation: "Drawings and materials submitted for permit review.",
  },
  {
    key: "detailed_design",
    label: "Detailed Design",
    stageTitle: "Detailed Design",
    description: "Detailed drawings and specifications are developed for construction.",
    tooltipExplanation: "Refined design and coordination before construction.",
  },
  {
    key: "construction",
    label: "Construction",
    stageTitle: "Construction",
    description: "Construction phase coordination and site-related deliverables.",
    tooltipExplanation: "Active construction support and documentation.",
  },
  {
    key: "construction_close_out",
    label: "Construction Close Out",
    stageTitle: "Construction Close Out",
    description: "Project closeout, final deliverables, and wrap-up.",
    tooltipExplanation: "Final documentation and project completion.",
  },
];

function safeStr(value) {
  return String(value ?? "").trim();
}

export function normalizeProjectJourneyStageKey(value) {
  const key = safeStr(value).toLowerCase();
  const match = PROJECT_JOURNEY_STAGE_DEFS.find((def) => def.key === key);
  return match ? match.key : PROJECT_JOURNEY_STAGE_DEFS[0].key;
}

export function normalizeProjectJourneyStageStatus(value) {
  const key = safeStr(value).toLowerCase();
  return Object.values(PROJECT_JOURNEY_STAGE_STATUS).includes(key)
    ? key
    : PROJECT_JOURNEY_STAGE_STATUS.IN_PROGRESS;
}

export function buildDefaultProjectJourneyControl() {
  return {
    currentStageKey: PROJECT_JOURNEY_STAGE_DEFS[0].key,
    currentStageStatus: PROJECT_JOURNEY_STAGE_STATUS.IN_PROGRESS,
    currentStageNote: "",
    waitingOnText: "",
    nextMilestoneText: "",
  };
}

export function normalizeProjectJourneyControl(raw = {}) {
  const base = buildDefaultProjectJourneyControl();
  if (!raw || typeof raw !== "object") return base;
  return {
    currentStageKey: normalizeProjectJourneyStageKey(raw.currentStageKey),
    currentStageStatus: normalizeProjectJourneyStageStatus(raw.currentStageStatus),
    currentStageNote: safeStr(raw.currentStageNote),
    waitingOnText: safeStr(raw.waitingOnText),
    nextMilestoneText: safeStr(raw.nextMilestoneText),
  };
}

function statusLabelForStageStatus(status) {
  return PROJECT_JOURNEY_STAGE_STATUS_LABELS[normalizeProjectJourneyStageStatus(status)] || "In progress";
}

function milestoneStateForIndex(index, currentIndex, currentStatus) {
  if (index < currentIndex) {
    return {
      state: JOURNEY_MILESTONE_STATE.COMPLETED,
      statusLabel: PROJECT_JOURNEY_STAGE_STATUS_LABELS.complete,
    };
  }
  if (index > currentIndex) {
    return {
      state: JOURNEY_MILESTONE_STATE.UPCOMING,
      statusLabel: PROJECT_JOURNEY_STAGE_STATUS_LABELS.not_started,
    };
  }

  const status = normalizeProjectJourneyStageStatus(currentStatus);
  if (status === PROJECT_JOURNEY_STAGE_STATUS.COMPLETE) {
    return {
      state: JOURNEY_MILESTONE_STATE.COMPLETED,
      statusLabel: PROJECT_JOURNEY_STAGE_STATUS_LABELS.complete,
    };
  }
  if (status === PROJECT_JOURNEY_STAGE_STATUS.NOT_STARTED) {
    return {
      state: JOURNEY_MILESTONE_STATE.UPCOMING,
      statusLabel: PROJECT_JOURNEY_STAGE_STATUS_LABELS.not_started,
    };
  }
  return {
    state: JOURNEY_MILESTONE_STATE.CURRENT,
    statusLabel: PROJECT_JOURNEY_STAGE_STATUS_LABELS.in_progress,
  };
}

/**
 * Build client-facing journey from admin-controlled pmProjects.projectJourney fields.
 */
export function buildAdminControlledProjectJourneyModel(project, context = {}) {
  if (!project) return null;

  const control = normalizeProjectJourneyControl(project.projectJourney);
  const defs = PROJECT_JOURNEY_STAGE_DEFS;
  const currentIndex = Math.max(
    0,
    defs.findIndex((def) => def.key === control.currentStageKey)
  );
  const currentDef = defs[currentIndex] || defs[0];
  const nextDef = currentIndex < defs.length - 1 ? defs[currentIndex + 1] : null;
  const progressPercent = computeJourneyProgressPercent(currentIndex, defs.length);

  const milestones = defs.map((def, index) => {
    const { state, statusLabel } = milestoneStateForIndex(
      index,
      currentIndex,
      control.currentStageStatus
    );
    return {
      key: def.key,
      label: def.label,
      state,
      statusLabel,
      tooltipTitle: def.label,
      tooltipStatus: statusLabel,
      tooltipExplanation: def.tooltipExplanation,
    };
  });

  const projectName = safeStr(context.projectName || project.name) || "Project";
  const thumbnailUrl =
    safeStr(context.thumbnailUrl) ||
    resolveProjectJourneyThumbnailUrl(project, context.projectFiles) ||
    null;

  const waitingOn = safeStr(control.waitingOnText) || null;
  const nextMilestoneLabel = safeStr(control.nextMilestoneText) || (nextDef ? nextDef.label : null);
  const description = safeStr(control.currentStageNote) || currentDef.description;

  return {
    projectId: safeStr(project.id) || null,
    projectName,
    milestones,
    currentMilestoneIndex: currentIndex,
    progressPercent,
    progressLabel: `${progressPercent}% complete`,
    isAdminControlled: true,
    currentStage: {
      eyebrow: "Current stage",
      title: currentDef.stageTitle,
      description,
      thumbnailUrl,
      waitingOn,
      waitingOnFallback: "Nothing needed from you right now.",
      nextMilestoneLabel,
      showReviewCta: false,
      reviewCtaLabel: "Review drawings",
      reviewAction: null,
    },
    journeyControl: control,
  };
}

export function buildProjectJourneyControlFirestorePatch(patch = {}) {
  const normalized = normalizeProjectJourneyControl({
    ...buildDefaultProjectJourneyControl(),
    ...patch,
  });
  return { projectJourney: normalized };
}
